import { Outlet, Link, useLocation } from 'react-router-dom'
import {
  HomeIcon,
  UsersIcon,
  CogIcon,
  LogOutIcon,
  MenuIcon,
  XIcon,
  KeyIcon,
  Calendar,
  ChevronDownIcon,
  ChevronRightIcon,
  FlaskConical,
  LucideIcon,
} from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'

interface NavChild {
  name: string
  href: string
}

interface NavItem {
  name: string
  icon: LucideIcon
  href?: string
  badge?: string
  children?: NavChild[]
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Tenants', href: '/tenants', icon: UsersIcon },
  { name: 'Users', href: '/users', icon: UsersIcon },
  { name: 'API Keys', href: '/keys', icon: KeyIcon },
  { name: 'Tasks', href: '/tasks', icon: Calendar },
  {
    name: 'Labs',
    icon: FlaskConical,
    badge: 'Alpha',
    children: [
      { name: 'LARS', href: '/labs/lars' },
      { name: 'SDL', href: '/labs/sdl' },
    ]
  },
  {
    name: 'Settings',
    icon: CogIcon,
    children: [
      { name: 'Admin Settings', href: '/settings' },
      { name: 'System Embeddings', href: '/system-embeddings' },
    ]
  },
]

export default function Layout() {
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    navigation.forEach(item => {
      if (item.children) {
        initial[item.name] = item.children.some(child =>
          location.pathname.startsWith(child.href)
        )
      }
    })
    return initial
  })
  const toggleGroup = (name: string) => {
    setOpenGroups(prev => ({ ...prev, [name]: !prev[name] }))
  }
  const { user, logout } = useAuth()

  const handleLogout = () => {
    logout()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex w-full max-w-xs flex-1 flex-col bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <XIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="h-0 flex-1 overflow-y-auto pt-5 pb-4">
            <div className="flex flex-shrink-0 items-center px-4">
              <h1 className="text-xl font-bold text-gray-900">Lawma Admin</h1>
            </div>
            <nav className="mt-5 space-y-1 px-2">
              {navigation.map((item) => {
                if (item.children) {
                  const isChildActive = item.children.some(child => location.pathname === child.href)
                  return (
                    <div key={item.name} className="space-y-1">
                      <button
                        onClick={() => toggleGroup(item.name)}
                        className={`${isChildActive
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          } group flex w-full items-center px-2 py-2 text-base font-medium rounded-md`}
                      >
                        <item.icon className="mr-4 h-6 w-6 flex-shrink-0" />
                        <span className="flex-1 text-left">
                          {item.name}
                          {item.badge && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700">
                              {item.badge}
                            </span>
                          )}
                        </span>
                        {openGroups[item.name] ? (
                          <ChevronDownIcon className="ml-3 h-5 w-5" />
                        ) : (
                          <ChevronRightIcon className="ml-3 h-5 w-5" />
                        )}
                      </button>
                      {openGroups[item.name] && (
                        <div className="space-y-1 pl-10">
                          {item.children.map((child) => (
                            <Link
                              key={child.name}
                              to={child.href}
                              onClick={() => setSidebarOpen(false)}
                              className={`${location.pathname === child.href
                                ? 'bg-gray-100 text-gray-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                } group flex items-center px-2 py-2 text-base font-medium rounded-md`}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                }

                const isActive = location.pathname === item.href
                return (
                  <Link
                    key={item.name}
                    to={item.href!}
                    onClick={() => setSidebarOpen(false)}
                    className={`${isActive
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      } group flex items-center px-2 py-2 text-base font-medium rounded-md`}
                  >
                    <item.icon className="mr-4 h-6 w-6 flex-shrink-0" />
                    {item.name}
                    {item.badge && (
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                )
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-white border-r border-gray-200">
          <div className="flex flex-1 flex-col overflow-y-auto pt-5 pb-4">
            <div className="flex flex-shrink-0 items-center px-4">
              <h1 className="text-xl font-bold text-gray-900">Lawma Admin</h1>
            </div>
            <nav className="mt-5 flex-1 space-y-1 px-2">
              {navigation.map((item) => {
                if (item.children) {
                  const isChildActive = item.children.some(child => location.pathname === child.href)
                  return (
                    <div key={item.name} className="space-y-1">
                      <button
                        onClick={() => toggleGroup(item.name)}
                        className={`${isChildActive
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          } group flex w-full items-center px-2 py-2 text-sm font-medium rounded-md`}
                      >
                        <item.icon className="mr-3 h-6 w-6 flex-shrink-0" />
                        <span className="flex-1 text-left">
                          {item.name}
                          {item.badge && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700">
                              {item.badge}
                            </span>
                          )}
                        </span>
                        {openGroups[item.name] ? (
                          <ChevronDownIcon className="ml-2 h-4 w-4" />
                        ) : (
                          <ChevronRightIcon className="ml-2 h-4 w-4" />
                        )}
                      </button>
                      {openGroups[item.name] && (
                        <div className="space-y-1 pl-9">
                          {item.children.map((child) => (
                            <Link
                              key={child.name}
                              to={child.href}
                              className={`${location.pathname === child.href
                                ? 'bg-gray-100 text-gray-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                } group flex items-center px-2 py-2 text-sm font-medium rounded-md`}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                }

                const isActive = location.pathname === item.href
                return (
                  <Link
                    key={item.name}
                    to={item.href!}
                    className={`${isActive
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      } group flex items-center px-2 py-2 text-sm font-medium rounded-md`}
                  >
                    <item.icon className="mr-3 h-6 w-6 flex-shrink-0" />
                    {item.name}
                    {item.badge && (
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                )
              })}
            </nav>
          </div>
          <div className="flex flex-shrink-0 border-t border-gray-200 p-4">
            <div className="w-full">
              <div className="text-xs text-gray-500 mb-2">
                {user?.email}
              </div>
              <button
                onClick={handleLogout}
                className="flex w-full items-center text-sm text-gray-600 hover:text-gray-900"
              >
                <LogOutIcon className="mr-3 h-5 w-5" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-1 flex-col">
        <div className="sticky top-0 z-10 bg-white pl-1 pt-1 sm:pl-3 sm:pt-3 lg:hidden">
          <button
            type="button"
            className="-ml-0.5 -mt-0.5 inline-flex h-12 w-12 items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
            onClick={() => setSidebarOpen(true)}
          >
            <MenuIcon className="h-6 w-6" />
          </button>
        </div>
        <main className="flex-1">
          <div className="py-6">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
